// ============================================================================
// Summary → Driver performance. Two figures over the same `visibleRows` the rest
// of the Summary counts, so the section moves with every filter above it:
//
//   REPORTED ON TIME — drivers whose vehicle reached before the morning cutoff.
//     Over a date RANGE the unit changes from drivers to DRIVER-DAYS: five drivers
//     across three days is up to fifteen mornings, and the tile counts how many of
//     them started before the cutoff. One driver's good week can then no longer
//     hide behind a single arrival, and the tile agrees with the "3 of 5 days"
//     tally the drawer shows per driver.
//   DELIVERY BREACHES — shipments of theirs that missed their SLA, and how many
//     drivers that is spread across.
//
// The SLA bar runs in THREE parts, not two: Breached | Excused | On time. On time
// is no longer everything that is not breached — a shipment excused by an
// escalation missed its cutoff all the same, and a complement would quietly file
// it as punctual.
//
// Both open the same drawer, which is the per-driver breakdown of exactly these
// two numbers. Arrival is still awaiting a source, so its figure is an em dash,
// not a 0 — the same stance At risk takes one section up. The TOTALS are real,
// because we genuinely know how many drivers are out and what they carried.
// ============================================================================
import { ClockCircleFilled, WarningFilled } from "@ant-design/icons";
import { Col, Row, Tooltip } from "antd";
import React from "react";
import {
  ARRIVAL_CUTOFF_LABEL,
  ARRIVAL_DATA_AVAILABLE,
  DriverPerformance,
  driverTotals,
} from "./driver_performance";
import styles from "./summary.module.scss";

interface Props {
  drivers: DriverPerformance[];
  // Whether the dispatch filter spans more than one day — drivers, or driver-days.
  multiDay: boolean;
  loading: boolean;
  onOpen: () => void;
}

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const DriverPerformanceSection: React.FC<Props> = ({ drivers, multiDay, loading, onOpen }) => {
  const {
    onTime: onTimeDrivers,
    onTimeDays,
    judgedDays,
    total: driverCount,
    breaches,
    excused,
    shipments,
    driversWithBreaches,
  } = driverTotals(drivers);
  const met = shipments - breaches - excused;
  // One filter, two units. Over a range the denominator is the mornings we heard about, not the
  // drivers — a driver who ran two of the five days is judged on those two and nothing else.
  const onTime = multiDay ? onTimeDays : onTimeDrivers;
  const total = multiDay ? judgedDays : driverCount;
  const unit = multiDay ? "driver-day" : "driver";
  const arrivalKnown = onTime !== null;
  const late = arrivalKnown ? total - onTime : null;

  // The same 8px mark / 24px hit area as the mile bar above, so the two sections read as one page.
  const segment = (count: number, cls: string, title: string) =>
    count > 0 ? (
      <Tooltip title={title}>
        <div className={styles.segHit} style={{ flexGrow: count }} role="button" onClick={onOpen}>
          <div className={`${styles.seg} ${cls}`} />
        </div>
      </Tooltip>
    ) : null;

  const legendItem = (label: string, count: number | null, dotCls: string) => (
    <span className={styles.legendItem} role="button" tabIndex={0} onClick={onOpen}>
      <span className={`${styles.dot} ${dotCls}`} />
      {label}
      <span className={count === null ? styles.slaUnknown : styles.legendValue}>
        {count === null ? "—" : count}
      </span>
    </span>
  );

  return (
    <>
      <div className={styles.sectionTitle}>
        Driver performance
        <span className={styles.sectionNote}>
          {multiDay
            ? "Per driver per day, not per shipment — each morning of the range is judged on its own."
            : "Per driver, not per shipment — a driver running twelve drops reports once."}{" "}
          On time means the vehicle arrived before {ARRIVAL_CUTOFF_LABEL}.
        </span>
      </div>
      <Row gutter={16} style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s ease" }}>
        {/* --- reporting punctuality --- */}
        <Col xs={24} lg={8} className={styles.col}>
          <div className={`${styles.tile} ${styles.clickable}`} role="button" onClick={onOpen}>
            <Tooltip
              title={
                ARRIVAL_DATA_AVAILABLE
                  ? multiDay
                    ? `Mornings that started before ${ARRIVAL_CUTOFF_LABEL}, counted per driver per day across this range — ${plural(driverCount, "driver")} ran it. Click to see how many days each of them was on time.`
                    : `Drivers whose vehicle arrived before ${ARRIVAL_CUTOFF_LABEL}. Click to list every driver and their arrival time.`
                  : `Arrival times are not wired to a source yet, so the on-time count is unknown. Click to see the ${plural(driverCount, "driver")} in scope.`
              }
            >
              <div className={styles.label}>
                <ClockCircleFilled className={styles.iconGood} />
                Reported on time
              </div>
            </Tooltip>
            <div className={styles.value}>
              {arrivalKnown ? onTime : <span className={styles.valueUnknown}>—</span>}
              <span className={styles.valueTotal}>of {total}</span>
            </div>
            <div className={styles.share}>
              {arrivalKnown
                ? `${pct(onTime, total)}% arrived before ${ARRIVAL_CUTOFF_LABEL}${
                    multiDay ? ` · ${plural(driverCount, "driver")}` : ""
                  }`
                : `${plural(total, "driver")} in the current filters`}
            </div>
            {arrivalKnown && total > 0 ? (
              <div className={styles.barRow}>
                {segment(onTime, styles.segOnTime, `On time — ${onTime} of ${plural(total, unit)}`)}
                {segment(late as number, styles.segLate, `Late — ${late} of ${plural(total, unit)}`)}
              </div>
            ) : (
              <div className={styles.barEmpty} />
            )}
            <div className={styles.legend}>
              {legendItem("On time", onTime, styles.dotOnTime)}
              {legendItem("Late", late, styles.dotLate)}
            </div>
          </div>
        </Col>

        {/* --- delivery SLA --- */}
        <Col xs={24} lg={8} className={styles.col}>
          <div className={`${styles.tile} ${styles.clickable}`} role="button" onClick={onOpen}>
            <Tooltip title="Shipments these drivers carried that missed their delivery SLA and are the transporter's to answer for. Shipments excused by an escalation are counted apart and not charged here. Click to see which driver each belongs to; the Breach column on the Trips tab gives the reason per shipment.">
              <div className={styles.label}>
                <WarningFilled className={styles.iconCritical} />
                Delivery breaches
              </div>
            </Tooltip>
            <div className={styles.value}>
              {breaches}
              <span className={styles.valueTotal}>of {shipments}</span>
            </div>
            <div className={styles.share}>
              {breaches === 0
                ? "No shipment has missed its SLA in this scope"
                : `across ${plural(driversWithBreaches, "driver")} of ${driverCount}`}
              {excused > 0 ? ` · ${excused} excused` : ""}
            </div>
            {shipments > 0 ? (
              <div className={styles.barRow}>
                {segment(
                  breaches,
                  styles.segLate,
                  `Breached — ${breaches} of ${plural(shipments, "shipment")}`
                )}
                {segment(
                  excused,
                  styles.segExcused,
                  `Excused — ${excused} of ${plural(shipments, "shipment")} missed their cutoff on an escalation that is not the transporter's`
                )}
                {segment(met, styles.segOnTime, `On time — ${met} of ${plural(shipments, "shipment")}`)}
              </div>
            ) : (
              <div className={styles.barEmpty} />
            )}
            <div className={styles.legend}>
              {legendItem("Breached", breaches, styles.dotLate)}
              {excused > 0 ? legendItem("Excused", excused, styles.dotExcused) : null}
              {legendItem("On time", met, styles.dotOnTime)}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DriverPerformanceSection;
